import { HydrationBoundary, QueryClientProvider } from "@tanstack/react-query";
import { getTransformer } from "@trpc/client/unstable-internals";
import { createReactDecoration, createReactQueryUtils, createRootHooks, getQueryClient } from "@trpc/react-query/shared";
import React, { useMemo, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { createFlatProxy } from "@trpc/server/unstable-core-do-not-import";

//#region src/withTRPC.tsx
function withTRPC(opts) {
	const { config: getClientConfig } = opts;
	const transformer = getTransformer(opts.transformer);
	return (AppOrPage) => {
		const trpc = createRootHooks(opts);
		const WithTRPC = (props) => {
			const [prepassProps] = useState(() => {
				if (props.trpc) return props.trpc;
				const config = getClientConfig({});
				const queryClient$1 = getQueryClient(config);
				const trpcClient$1 = trpc.createClient(config);
				return {
					abortOnUnmount: config.abortOnUnmount,
					queryClient: queryClient$1,
					trpcClient: trpcClient$1,
					ssrState: opts.ssr ? "mounting" : false,
					ssrContext: null
				};
			});
			const { queryClient, trpcClient, ssrState, ssrContext } = prepassProps;
			const trpcState = props.pageProps?.trpcState;
			const hydratedState = React.useMemo(() => {
				if (!trpcState) return trpcState;
				return transformer.input.deserialize(trpcState);
			}, [trpcState]);
			return /* @__PURE__ */ jsx(trpc.Provider, {
				abortOnUnmount: prepassProps.abortOnUnmount ?? false,
				client: trpcClient,
				queryClient,
				ssrState,
				ssrContext,
				children: /* @__PURE__ */ jsx(QueryClientProvider, {
					client: queryClient,
					children: /* @__PURE__ */ jsx(HydrationBoundary, {
						state: hydratedState,
						children: /* @__PURE__ */ jsx(AppOrPage, { ...props })
					})
				})
			});
		};
		if (opts.ssr) opts.ssrPrepass({
			parent: opts,
			AppOrPage,
			WithTRPC
		});
		else if (AppOrPage.getInitialProps) WithTRPC.getInitialProps = async (appOrPageCtx) => {
			const isApp = !!appOrPageCtx.Component;
			let pageProps = {};
			const originalProps = await AppOrPage.getInitialProps(appOrPageCtx);
			const originalPageProps = isApp ? originalProps.pageProps ?? {} : originalProps;
			pageProps = {
				...originalPageProps,
				...pageProps
			};
			const getAppTreeProps = (props) => isApp ? { pageProps: props } : props;
			return getAppTreeProps(pageProps);
		};
		const displayName = AppOrPage.displayName ?? AppOrPage.name ?? "Component";
		WithTRPC.displayName = `withTRPC(${displayName})`;
		return WithTRPC;
	};
}

//#endregion
//#region src/createTRPCNext.tsx
function createTRPCNext(opts) {
	const hooks = createRootHooks(opts);
	const _withTRPC = withTRPC(opts);
	const proxy = createReactDecoration(hooks);
	return createFlatProxy((key) => {
		if (key === "useContext" || key === "useUtils") return () => {
			const context = hooks.useUtils();
			return useMemo(() => {
				return createReactQueryUtils(context);
			}, [context]);
		};
		if (key === "useQueries") return hooks.useQueries;
		if (key === "useSuspenseQueries") return hooks.useSuspenseQueries;
		if (key === "withTRPC") return _withTRPC;
		return proxy[key];
	});
}

//#endregion
export { createTRPCNext, withTRPC };
//# sourceMappingURL=index.mjs.map